import { createApp } from "vue";
import { installStaleChunkReload } from "@/utils/staleChunk";
import App from "@/App.vue";
import "@/assets/styles/index.css";
import router from "@/router";
import { createGtm } from "@gtm-support/vue-gtm";
import { createHead } from "@unhead/vue/client";
import {
  QueryClient,
  VueQueryPlugin,
  VueQueryPluginOptions,
} from "@tanstack/vue-query";
import axios from "axios";
import {
  isBackendOverloadError,
  isOrganizationChangedError,
  isOrganizationMismatchError,
} from "@/services/queryRetry";
import { installOrganizationResync } from "@/services/organizationResync";
import { installAreaViewTracking } from "@/services/activity";
import { createPinia } from "pinia";
import FloatingVue from "floating-vue";

// Below is anything that should happen on app "boot/startup"

installStaleChunkReload();

const app = createApp(App);

// Production environment
import {
  init,
  browserTracingIntegration,
  extraErrorDataIntegration,
  consoleLoggingIntegration,
  setTag,
} from "@sentry/vue";

if (import.meta.env.VITE_APP_SENTRY_DSN) {
  init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN as string,
    release: `fyr@${import.meta.env.VITE_APP_VERSION}`,
    environment: import.meta.env.MODE,
    integrations: [
      extraErrorDataIntegration({ depth: 7 }),
      browserTracingIntegration({
        router,
      }),
      consoleLoggingIntegration({
        levels: ["error", "warn"],
      }),
    ],
    normalizeDepth: 8,
    tracesSampleRate: 0.1,
    _experiments: { enableLogs: true },
  });
  setTag("layer", "frontend");
}

// All environments
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import "@egjs/vue3-flicking/dist/flicking.css";

const head = createHead();

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          if (
            axios.isAxiosError(error) &&
            (error.response?.status === 404 || error.response?.status === 429)
          ) {
            return false;
          }
          if (isBackendOverloadError(error)) {
            return false;
          }
          if (isOrganizationChangedError(error)) {
            return false;
          }
          // Deterministic: the same request is refused identically until this
          // tab's selected organization catches up. installOrganizationResync
          // refetches the profile, which re-keys the query to the real
          // organization; retrying the stale key just delays that.
          if (isOrganizationMismatchError(error)) {
            return false;
          }
          return failureCount < 3;
        },
        staleTime: 10 * 1000,
      },
    },
  },
};

// Owned explicitly rather than left to the plugin, so the resync subscriber
// below attaches to the same cache the app uses.
const queryClient = new QueryClient(vueQueryPluginOptions.queryClientConfig);
installOrganizationResync(queryClient);
// Before app.use(router), whose initial navigation it has to see.
installAreaViewTracking(router, queryClient);

// Create GTM plugin without automatic router tracking
const gtmPlugin = createGtm({
  id: "GTM-NJ6Q4TZ",
  defer: false,
  compatibility: false,
  enabled: import.meta.env.MODE === "production",
  debug: import.meta.env.MODE !== "production",
  // Don't pass vueRouter to prevent automatic tracking
  // We'll implement throttled tracking manually
});

app
  .use(router)
  .use(FloatingVue, {
    // https://floating-vue.starpad.dev/guide/config.html#default-values
  })
  .use(gtmPlugin)
  .use(head)
  .use(VueQueryPlugin, { queryClient })
  .use(createPinia());

// Track actual route changes only (not filter changes)
// This prevents GTM spam when filters cause reactive updates
let lastTrackedRoute: string | null = null;

router.afterEach((to) => {
  const gtm = app.config.globalProperties.$gtm;
  if (gtm && to.name) {
    const currentRoute = to.path;

    // Only track if the route path actually changed (not query params or hash)
    if (currentRoute !== lastTrackedRoute) {
      lastTrackedRoute = currentRoute;
      if (gtm.trackView) {
        gtm.trackView(String(to.name), currentRoute);
      }
    }
  }
});

app.mount("#app");
